import { cascadeOptions, configuratorState, triggerActive, CONFIGURATOR_ORDER } from '@icontactic/logic';

/**
 * F3E2-04: initial options straight from the purchase gate's server-rendered JSON, so the component's first
 * render does not depend on module execution order (the gate broadcasts `ict:options-change` only once).
 */
function initialOptionsFrom(root) {
  try {
    const payload = root.querySelector('ict-purchase-gate script[type="application/json"]');
    if (!payload) return null;
    const cfg = JSON.parse(payload.textContent || '{}');
    const names = cfg.optionNames || [];
    const values = cfg.selected?.options || [];
    const out = {};
    names.forEach((n, i) => { if (values[i] !== undefined) out[n] = values[i]; });
    return out;
  } catch { return null; }
}

/**
 * <ict-configurator> — compatibility configurator (P03/P04 architecture, docs/05b).
 * Data: metaobject-driven records injected by Liquid as JSON (fixture JSON in development only).
 * Behaviour: cascading selects, unsupported combinations never offered, fails closed, writes line-item
 * properties via the purchase gate. `_holster_sku` is fulfillment metadata only — never inventory.
 */
class IctConfigurator extends HTMLElement {
  #records = [];
  #selection = {};
  #active = true;
  #abort = new AbortController();

  connectedCallback() {
    const data = this.querySelector('script[type="application/json"]');
    const parsed = data ? JSON.parse(data.textContent || '{}') : {};
    this.#records = parsed.records || [];
    this.profileStatus = parsed.profileStatus || 'missing';
    this.propertyNames = parsed.propertyNames || {};
    this.trigger = { option: this.dataset.triggerOption || '', value: this.dataset.triggerValue || '' };
    this.messages = parsed.messages || {};

    this.selects = {};
    CONFIGURATOR_ORDER.forEach((key) => {
      this.selects[key] = this.querySelector(`select[data-ict-key="${key}"]`);
    });
    this.messageEl = this.querySelector('[data-ict-message]');

    const { signal } = this.#abort;
    this.addEventListener('change', this.#onChange, { signal });
    const root = this.closest('[data-ict-scope]') || this.closest('.shopify-section') || document.body;
    root.addEventListener('ict:options-change', this.#onOptions, { signal });
    root.addEventListener('ict:gate-ready', () => this.#update(), { signal });
    const initial = initialOptionsFrom(root);
    if (initial) this.#active = triggerActive(this.trigger, initial);

    this.#render();
    this.#emit();
  }

  disconnectedCallback() { this.#abort.abort(); }

  /** F3E2-04: re-render and re-announce state (the gate may have connected after this component). */
  #update() {
    this.#render();
    this.#emit();
  }

  #onOptions = (event) => {
    const active = triggerActive(this.trigger, event.detail?.options);
    if (active !== this.#active) {
      this.#active = active;
      if (!active) this.#selection = {}; // clear values when hidden (Bag only)
      this.#render();
      this.#emit();
    }
  };

  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const key = select.dataset.ictKey;
    if (!key) return;
    const depth = CONFIGURATOR_ORDER.indexOf(key);
    this.#selection[key] = select.value || '';
    // Reset every deeper level: a change upstream invalidates downstream choices
    CONFIGURATOR_ORDER.slice(depth + 1).forEach((k) => { this.#selection[k] = ''; });
    this.#render();
    this.#emit();
  };

  #render() {
    this.hidden = !this.#active;
    this.classList.toggle('ict-hidden-important', !this.#active);
    const available = cascadeOptions(this.#records, this.#selection);
    CONFIGURATOR_ORDER.forEach((key, depth) => {
      const select = this.selects[key];
      if (!select) return;
      const prevKey = CONFIGURATOR_ORDER[depth - 1];
      const enabled = depth === 0 || !!this.#selection[prevKey];
      const values = available[key] || [];
      const placeholder = select.dataset.placeholder || '';
      const current = this.#selection[key] || '';
      select.replaceChildren();
      const opt0 = document.createElement('option');
      opt0.value = '';
      opt0.textContent = placeholder;
      select.appendChild(opt0);
      for (const v of values) {
        const opt = document.createElement('option');
        opt.value = v;
        opt.textContent = this.#label(key, v);
        if (v === current) opt.selected = true;
        select.appendChild(opt);
      }
      select.disabled = !enabled || values.length === 0 || this.profileStatus !== 'verified';
      select.setAttribute('aria-invalid', String(enabled && !current && this.#active));
    });
    const state = this.state;
    if (this.messageEl) {
      let text = '';
      if (this.profileStatus !== 'verified') text = this.messages.pending || '';
      else if (state.complete && !state.valid) text = this.messages.unsupported || '';
      else if (!state.complete) text = this.messages.hint || '';
      this.messageEl.textContent = text;
      this.messageEl.classList.toggle('ict-field__error', state.complete && !state.valid);
    }
  }

  #label(key, value) {
    const labels = this.messages.values?.[key];
    return (labels && labels[value]) || value;
  }

  get state() {
    if (this.profileStatus !== 'verified') {
      return { complete: false, valid: false, code: 'configuration_required', properties: {}, record: null };
    }
    return configuratorState(this.#records, this.#selection, this.propertyNames);
  }

  #emit() {
    const s = this.state;
    this.dispatchEvent(
      new CustomEvent('ict:component-state', {
        bubbles: true,
        detail: {
          id: this.id,
          kind: 'configurator',
          active: this.#active,
          required: true,
          valid: s.valid,
          code: s.code,
          message: s.code === 'configuration_unsupported' ? this.messages.unsupported : this.messages.required,
          properties: s.properties,
          trigger: this.trigger,
        },
      })
    );
  }
}

if (!customElements.get('ict-configurator')) customElements.define('ict-configurator', IctConfigurator);
